/**
 * AuthLayout.jsx — AIRGROW Material Interface
 * 認証ページ共通レイアウト（ログイン・新規登録）
 * 背景画像 + 左ペイン + 右ドロワー の2カラム構成。
 * ドロワーのスライドインアニメーションを内部で管理する。
 *
 * exports:
 *   default        AuthLayout        2カラムレイアウト
 *   AuthBg         背景レイヤーのみ（overlay 画面でも使用可）
 *   AuthSubmitButton  認証ページ専用プライマリボタン
 */
import React, { useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { C } from '../../ColorTokens';
import AirgrowBrand from './AirgrowBrand';

// TODO: ローカルアセットに差し替える
const BG_IMAGE =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuA45DUPVq3TIQaY2vTSPwrdQNtGJg8BKMdfaV4angoBjBGBfX5LosQh5kNWaL6-5pXNgWh_pacjJULfRZCgH-1L9L6yM__ybzULghULLBtbH4_H0xDr40zG1kG7HQU__wdGK_SNdEfDepfTc_xGTCPpmKWBPf8jr2Qg8lB2GyGgLHb2aJk18-FX41RJyLrECb0CCU47dUlAq1u_5gbqE6kWOeaHmktXtk_GwDMsIGqnezhLOKfzAyLfJTgJwzHDRfk4drh7w9Enfec';

// ---- 背景レイヤー ----
// 単体でも使用可（overlay 画面 など）
export function AuthBg({ overlay = 'rgba(0,80,142,0.72)' }) {
  return (
    <>
      <div className="absolute inset-0 z-0 flex overflow-hidden">
        <img src={BG_IMAGE} alt="" className="flex-shrink-0" style={{ height: '100%', width: 'auto' }} />
        <img src={BG_IMAGE} alt="" className="flex-shrink-0" style={{ height: '100%', width: 'auto', transform: 'scaleX(-1)' }} />
      </div>
      <div className="absolute inset-0 z-[1]" style={{ backgroundColor: overlay }} />
    </>
  );
}

// ---- 認証ページ専用プライマリボタン ----
// ローディング中: progress_activity スピナー
// 通常時: arrow_forward（hover で右へ移動）
export function AuthSubmitButton({ loading, disabled, onClick, type = 'submit', children }) {
  const isDisabled = loading || disabled;
  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      className="w-full flex items-center justify-center gap-3 group active:scale-95 transition-all disabled:opacity-60"
      style={{
        backgroundColor: isDisabled ? C.outline : C.primary,
        color: '#fff',
        fontFamily: 'Space Grotesk',
        fontSize: '16px',
        fontWeight: 600,
        padding: '16px',
        borderRadius: '9999px',
        border: 'none',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        boxShadow: isDisabled ? 'none' : '0 8px 24px rgba(0,80,142,0.28)',
        transition: 'background-color 0.2s',
      }}
    >
      <span>{children}</span>
      {loading
        ? <span className="material-symbols-outlined" style={{ fontSize: '20px', animation: 'spin 1s linear infinite' }}>progress_activity</span>
        : <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: '20px' }}>arrow_forward</span>
      }
    </button>
  );
}


// ---- メインレイアウト ----
// leftHeading / leftBody には JSX を渡す（<br /> 等を含む場合があるため）
export default function AuthLayout({ leftHeading, leftBody, children }) {
  const drawerRef = useRef(null);
  const location = useLocation();

  // ページ遷移のたびに右ドロワーをスライドインさせる
  // useLayoutEffect + DOM ref で直接制御。
  // ペイント前に同期実行されるため React のレンダリングサイクルや
  // StrictMode の二重起動に影響されず、常に確実にアニメーションが再生される。
  useLayoutEffect(() => {
    const el = drawerRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = 'translateX(100%)';
    el.style.opacity = '0';
    void el.getBoundingClientRect();
    el.style.transition = 'transform 1.2s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.9s ease-out';
    el.style.transform = 'translateX(0)';
    el.style.opacity = '1';
  }, [location.key]);

  return (
    <div className="h-screen relative overflow-hidden">
      <AuthBg />

      {/* 左ペイン（デスクトップのみ）*/}
      <div className="hidden lg:block absolute inset-y-0 left-0 z-10" style={{ width: '60%' }}>
        <div
          className="relative h-full flex flex-col justify-center text-white"
          style={{ padding: '0 64px', maxWidth: '640px' }}
        >
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontSize: 'clamp(52px, 6vw, 80px)',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              marginBottom: '24px',
              opacity: 0.92,
            }}
          >
            {leftHeading}
          </h1>
          <p
            style={{
              fontFamily: 'Noto Sans JP',
              fontSize: '17px',
              fontWeight: 300,
              lineHeight: 1.8,
              opacity: 0.75,
            }}
          >
            {leftBody}
          </p>
        </div>
      </div>

      {/* 右ドロワー */}
      <div
        ref={drawerRef}
        className="absolute inset-y-0 right-0 z-20 w-full lg:w-1/2 flex flex-col items-center"
        style={{
          backgroundColor: C.background,
          borderRadius: '8rem 0 0 8rem',
          boxShadow: '-32px 0 80px rgba(0,0,0,0.20)',
          padding: '40px 48px',
          overflowY: 'auto',
        }}
      >
        {/* コンテンツ: 縦方向に伸張してコピーライトを末尾へ押し出す */}
        <div
          style={{
            width: '100%',
            maxWidth: '480px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div style={{ marginBottom: '32px' }}>
            <AirgrowBrand size="md" layout="vertical" />
          </div>
          {children}
        </div>

        {/* Copyright */}
        <div
          style={{
            width: '100%',
            maxWidth: '480px',
            paddingTop: '24px',
            textAlign: 'center',
            fontFamily: 'Space Grotesk',
            fontSize: '10px',
            letterSpacing: '0.3px',
            color: C.outline,
          }}
        >
          © 2026 NTT e-Drone Technology Co., Ltd. All rights reserved.
        </div>
      </div>
    </div>
  );
}
